/**
 * PromiseWorker provides a Promise-based postMessage API over Web Workers.
 * It borrows a worker from the pool on demand and returns it when idle.
 * React components hold a PromiseWorker in a ref and call terminate() on
 * unmount or before starting new work.
 *
 * @param {() => Worker} onCheckout - borrows a worker from the pool
 * @param {(worker: Worker) => void} onRelease - returns a worker to the pool
 */
class PromiseWorker {
    #worker = null;
    #onCheckout;
    #onRelease;
    /** @type {{ resolve: Function, reject: Function, data: * }[]} */
    #queue = [];
    #processing = false;

    constructor(onCheckout, onRelease) {
        this.#onCheckout = onCheckout;
        this.#onRelease = onRelease;
    }

    #onMessage = event => {
        if (this.#processing) {
            this.#processing.resolve(event.data);
        }
        if (this.#queue.length > 0) {
            this.#sendNext();
        } else {
            this.#release();
        }
    };

    /**
     * @param {WorkerEventMap['error']} event
     * @memberof PromiseWorker
     */
    #onError = event => {
        if (this.#processing) {
            this.#processing.reject(event.error || event.message || event);
        }
        if (this.#queue.length > 0) {
            this.#sendNext();
        } else {
            this.#release();
        }
    };

    #sendNext() {
        this.#processing = this.#queue.shift();
        this.#worker.postMessage(this.#processing.data);
    }

    #release() {
        if (!this.#worker) {
            return;
        }
        this.#processing = false;
        this.#worker.removeEventListener('message', this.#onMessage);
        this.#worker.removeEventListener('error', this.#onError);
        this.#onRelease(this.#worker);
        this.#worker = null;
    }

    /**
     * Post a message to the worker and return a Promise for the response.
     * Borrows a worker from the pool if idle. Messages are queued and
     * processed sequentially in FIFO order.
     *
     * @param {*} data
     * @return {Promise<*>}
     */
    postMessage(data) {
        return new Promise((resolve, reject) => {
            this.#queue.push({ resolve, reject, data });
            if (!this.#worker) {
                this.#worker = this.#onCheckout();
                this.#worker.addEventListener('message', this.#onMessage);
                this.#worker.addEventListener('error', this.#onError);
            }
            if (!this.#processing) {
                this.#sendNext();
            }
        });
    }

    /**
     * Terminate the underlying worker immediately, rejecting any pending
     * promises. Use this on component unmount or before reassigning work.
     * The PromiseWorker remains usable — the next postMessage() will
     * borrow a fresh worker from the pool.
     */
    terminate() {
        if (!this.#worker || !this.#processing) {
            return;
        }
        this.#worker.removeEventListener('message', this.#onMessage);
        this.#worker.removeEventListener('error', this.#onError);
        this.#worker.terminate();
        if (this.#processing) {
            this.#processing.reject(new Error('PromiseWorker terminated'));
        }
        for (const { reject } of this.#queue) {
            reject(new Error(`PromiseWorker terminated with pending message`));
        }
        this.#queue.length = 0;
        this.#processing = false;
        this.#worker = null;
    }
}

/**
 * WorkerClient manages a pool of Web Workers for a given script URL
 * and creates PromiseWorker instances that borrow workers from the pool.
 *
 * Usage:
 *   const client = new WorkerClient('/assets/js/rulematch-worker.js');
 *   const pw = client.create();
 *   const result = await pw.postMessage({ type, lookup, value });
 *   const result2 = await pw.postMessage({ type, lookup, value });
 *   pw.terminate(); // on unmount or before creating a new one
 */
export class WorkerClient {
    /** @type {Set<Worker>} */
    #pool = new Set();
    #maxWorkers;
    #url;

    /**
     * @param {string} url - URL to the worker script
     * @param {{ maxWorkers?: number }} [options]
     */
    constructor(url, options = {}) {
        this.#url = url;
        const hardwareConcurrency =
            typeof navigator !== 'undefined' ? navigator.hardwareConcurrency : undefined;
        this.#maxWorkers = options.maxWorkers ?? hardwareConcurrency ?? 4;
    }

    #checkout() {
        if (this.#pool.size > 0) {
            const worker = this.#pool.values().next().value;
            this.#pool.delete(worker);
            return worker;
        }
        return new Worker(this.#url);
    }

    #checkIn(worker) {
        if (this.#pool.size < this.#maxWorkers) {
            this.#pool.add(worker);
        } else {
            worker.terminate();
        }
    }

    /**
     * Create a new PromiseWorker. Workers are borrowed from the pool
     * on demand and returned when idle. Calling terminate() destroys
     * the in-flight worker but the PromiseWorker remains reusable.
     *
     * @return {PromiseWorker}
     */
    create() {
        return new PromiseWorker(this.#checkout.bind(this), this.#checkIn.bind(this));
    }
}
